import { Plus } from 'lucide-react';
import ForecastItemCompactRow from './ForecastItemCompactRow.jsx';
import { formatCurrency } from '../../lib/format.js';
import { sumCategoryMonthlyCents } from '../../lib/forecastHelpers.js';

/**
 * Card compacte pour une catégorie feuille.
 * Affiche le nom, le total mensuel, le bouton + et les items en lignes denses.
 *
 * Props :
 *   category                — objet catégorie avec items[]
 *   onAddItem               — (categoryId, categoryName, flowType) => void
 *   onEditItem              — (item) => void
 *   onDeleteItem            — (item) => void (optionnel)
 *   onOpenDrawer            — (item) => void — ProvisionDetailDrawer
 *   onOpenRecurrenceHistory — (item) => void — RecurrenceTransactionsPanel
 */
export default function ForecastCategoryCard({
  category,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onOpenDrawer,
  onOpenRecurrenceHistory,
}) {
  const items = category.items ?? [];
  const totalCents = sumCategoryMonthlyCents(category);
  const isIncome = category.flow_type === 'income';

  const totalDisplay = totalCents !== 0
    ? (isIncome ? '+' : '') + formatCurrency(Math.abs(totalCents))
    : '—';
  const totalColorClass = totalCents === 0
    ? 'text-slate-400'
    : isIncome
    ? 'text-green-600'
    : 'text-red-600';

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      {/* Header catégorie */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
        <span className="flex-1 text-sm font-semibold text-slate-700 truncate">
          {category.name}
        </span>
        <span className={`shrink-0 text-xs font-semibold tabular-nums ${totalColorClass}`}>
          {totalDisplay}
          {totalCents !== 0 && (
            <span className="text-[10px] font-normal text-slate-400 ml-0.5">/mois</span>
          )}
        </span>
        <button
          type="button"
          onClick={() => onAddItem?.(category.id, category.name, category.flow_type)}
          className="shrink-0 p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          aria-label={`Ajouter un prévisionnel dans ${category.name}`}
          title="Ajouter un prévisionnel"
        >
          <Plus size={14} />
        </button>
      </div>

      {/* Items */}
      {items.length > 0 ? (
        <div className="px-1 py-1 divide-y divide-slate-50">
          {items.map((item) => (
            <ForecastItemCompactRow
              key={`${item.mode}-${item.id}`}
              item={item}
              onEdit={onEditItem}
              onDelete={onDeleteItem}
              onOpenDrawer={onOpenDrawer}
              onOpenHistory={onOpenRecurrenceHistory}
            />
          ))}
        </div>
      ) : (
        <div className="px-3 py-2.5 flex items-center gap-1.5 text-xs text-slate-400">
          <span>Aucun prévisionnel —</span>
          <button
            type="button"
            onClick={() => onAddItem?.(category.id, category.name, category.flow_type)}
            className="text-slate-500 hover:text-slate-800 underline underline-offset-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 rounded"
          >
            + Ajouter
          </button>
        </div>
      )}
    </div>
  );
}
